/**
 * # useScrollPosition
 * Track scroll position of window and element (optional)
 *
 * @example
 * useScrollPosition(
    ({ prevPos, currPos }) => {
      const isShow = currPos.window.y > prevPos.window.y
      if (isShow !== hideOnScroll) setHideOnScroll(isShow)
    },
    [hideOnScroll],
    null,
    50
  )
 *
 * @param {code} effect - code to run immediately after DOM has been updated, but before browser is hydrated
 * @param {stateful variables} deps - to force re-render only on their state changes. [] by default
 * @param {element} element - reference element to get the scroll position
 * @param {number} wait - a time period in ms to throttle the function
 *
 */

import { useRef, DependencyList, MutableRefObject } from "react";
import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect";

interface IPosition {
  x: number;
  y: number;
}

interface IVariantsPosition {
  element?: IPosition;
  window: IPosition;
}

interface IScrollProps {
  prevPos: IVariantsPosition;
  currPos: IVariantsPosition;
}

type ElementRef = MutableRefObject<HTMLElement | undefined>;

const isBrowser = typeof window !== `undefined`;
const zeroPosition = { element: { x: 0, y: 0 }, window: { x: 0, y: 0 } };
const zeroWindowPosition = { window: { x: 0, y: 0 } };

const getClientRect = (element?: HTMLElement): DOMRect | undefined =>
  element?.getBoundingClientRect();

const getScrollPosition = (element?: ElementRef): IVariantsPosition => {
  if (!isBrowser) {
    return zeroPosition;
  }

  if (element && element.current === null) {
    return zeroWindowPosition;
  }

  const targetPosition = getClientRect(element?.current || document.body);

  return element
    ? {
        element: {
          // used to handle when window scroll >= element position
          x: targetPosition?.left || 0,
          y: targetPosition?.top || 0,
        },
        window: {
          x: window.scrollX,
          y: window.scrollY,
        },
      }
    : {
        window: {
          x: window.scrollX,
          y: window.scrollY,
        },
      };
};

export const useScrollPosition = (
  effect: (props: IScrollProps) => void,
  deps?: DependencyList,
  element?: ElementRef,
  wait?: number
): void => {
  const position = useRef(getScrollPosition());

  let throttleTimeout: number | null = null;

  const callBack = (): void => {
    if (element && element.current === null) return;
    const currPos = element ? getScrollPosition(element) : getScrollPosition();
    effect({ prevPos: position.current, currPos });
    position.current = currPos;
    throttleTimeout = null;
  };

  useIsomorphicLayoutEffect(() => {
    if (!isBrowser) {
      return undefined;
    }

    const handleScroll = (): void => {
      if (wait) {
        if (throttleTimeout === null) {
          throttleTimeout = window.setTimeout(callBack, wait);
        }
      } else {
        callBack();
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("orientationchange", handleScroll);
    window.addEventListener("resize", handleScroll);

    return (): void => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("orientationchange", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, deps);
};
